/**
 * Canvas.js
 *
 * El lienzo: dibuja el árbol, gestiona selección, hover, drag & drop y las
 * acciones flotantes de cada bloque.
 *
 * El drag & drop es HTML5 nativo a propósito — sin librerías. Con el modelo de
 * anidamiento fijo (sección › fila › columna › elemento) las reglas de dónde
 * puede caer cada cosa son tan estrictas que una librería genérica agregaría más
 * peso que ayuda, y el indicador de posición lo dibujamos igual a mano.
 */

import React, { useState, useRef, useCallback } from 'react';
import ElementRenderer from './ElementRenderer';
import { effectiveStyle, toReactStyle, canDrop, CONTAINER_TYPES } from './builderModel';

const BP_WIDTH = { desktop: '100%', tablet: '1024px', mobile: '390px' };

const TYPE_LABEL = {
  section: 'Sección', row: 'Fila', column: 'Columna',
  heading: 'Título', text: 'Texto', button: 'Botón', image: 'Imagen', video: 'Video',
  icon: 'Icono', divider: 'Separador', spacer: 'Espacio', list: 'Lista', form: 'Formulario',
  booking: 'Agendar', map: 'Mapa', social: 'Redes', html: 'HTML', countdown: 'Contador',
  testimonial: 'Testimonio', pricing: 'Plan', faq: 'Preguntas'
};

function hiddenAt(node, bp) {
  const a = node.advanced || {};
  if (bp === 'desktop') return !!a.hideDesktop;
  if (bp === 'tablet') return !!a.hideTablet;
  return !!a.hideMobile;
}

/* ------------------------------------------------------------------ *
 * Nodo
 * ------------------------------------------------------------------ */

function NodeView(props) {
  const {
    node, parentType, breakpoint, selectedId, hoverId, drag,
    onSelect, onHover, onDragState, onDrop, actions, siteSlug, bookingSlug
  } = props;

  const [dropEdge, setDropEdge] = useState(null); // 'before' | 'after' | 'inside'
  const ref = useRef(null);

  const selected = selectedId === node.id;
  const hovered = hoverId === node.id && !selected;
  const isContainer = CONTAINER_TYPES.includes(node.type);
  const style = toReactStyle(effectiveStyle(node, breakpoint));
  const invisible = hiddenAt(node, breakpoint);

  const handleDragOver = (e) => {
    const payload = drag.current;
    if (!payload) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = ref.current.getBoundingClientRect();
    const type = payload.nodeType;

    // Si el tipo arrastrado cabe DENTRO de este nodo y el cursor está en el centro,
    // se ofrece "adentro"; en los bordes, antes/después. Es el gesto que la gente
    // ya conoce de cualquier editor.
    const canInside = isContainer && canDrop(type, node.type);
    const canSibling = canDrop(type, parentType);

    const relY = (e.clientY - rect.top) / rect.height;
    let edge = null;
    if (canInside && relY > 0.25 && relY < 0.75) edge = 'inside';
    else if (canSibling) edge = relY < 0.5 ? 'before' : 'after';
    else if (canInside) edge = 'inside';

    setDropEdge(edge);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setDropEdge(null);
  };

  const handleDrop = (e) => {
    if (!drag.current || !dropEdge) return;
    e.preventDefault();
    e.stopPropagation();
    onDrop(node.id, dropEdge);
    setDropEdge(null);
  };

  const cls = [
    'bld-node', 'bld-' + node.type,
    selected ? 'is-selected' : '', hovered ? 'is-hover' : '',
    invisible ? 'is-hidden-bp' : '',
    dropEdge ? 'drop-' + dropEdge : ''
  ].filter(Boolean).join(' ');

  const isEmptyContainer = isContainer && !(node.children || []).length;

  return (
    <div
      ref={ref}
      className={cls}
      style={style}
      id={node.props?.anchor || undefined}
      onClick={(e) => { e.stopPropagation(); onSelect(node.id); }}
      onMouseOver={(e) => { e.stopPropagation(); onHover(node.id); }}
      onMouseOut={(e) => { e.stopPropagation(); onHover(null); }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {(selected || hovered) && (
        <div className="bld-tag">
          <span
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              onDragState({ kind: 'move', id: node.id, nodeType: node.type });
              e.dataTransfer.effectAllowed = 'move';
              // Firefox no arranca el drag sin datos en el dataTransfer.
              e.dataTransfer.setData('text/plain', node.id);
            }}
            onDragEnd={() => onDragState(null)}
            className="bld-tag-grip"
            title="Arrastrar"
          >⣿</span>
          {TYPE_LABEL[node.type] || node.type}
          {invisible ? <em title="Oculto en esta vista"> · oculto</em> : null}
          {selected && (
            <span className="bld-tag-actions">
              <button type="button" title="Subir" onClick={(e) => { e.stopPropagation(); actions.move(node.id, -1); }}>↑</button>
              <button type="button" title="Bajar" onClick={(e) => { e.stopPropagation(); actions.move(node.id, 1); }}>↓</button>
              <button type="button" title="Duplicar" onClick={(e) => { e.stopPropagation(); actions.duplicate(node.id); }}>⧉</button>
              {node.type === 'section' && (
                <button type="button" title="Guardar como plantilla" onClick={(e) => { e.stopPropagation(); actions.saveTemplate(node.id); }}>★</button>
              )}
              <button type="button" title="Eliminar" className="danger" onClick={(e) => { e.stopPropagation(); actions.remove(node.id); }}>✕</button>
            </span>
          )}
        </div>
      )}

      {isContainer ? (
        <>
          {(node.children || []).map((child) => (
            <NodeView key={child.id} {...props} node={child} parentType={node.type} />
          ))}
          {isEmptyContainer && (
            <div className="bld-empty">
              {node.type === 'column' ? 'Arrastra un bloque aquí' :
                node.type === 'row' ? 'Fila sin columnas' : 'Sección vacía'}
            </div>
          )}
        </>
      ) : (
        <ElementRenderer
          node={node}
          breakpoint={breakpoint}
          editable={selected}
          onProps={actions.setProps}
          siteSlug={siteSlug}
          bookingSlug={bookingSlug}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Lienzo
 * ------------------------------------------------------------------ */

export default function Canvas({
  tree, theme, breakpoint, selectedId, onSelect, actions, drag, onDragState, onDrop,
  siteSlug, bookingSlug, cssVars
}) {
  const [hoverId, setHoverId] = useState(null);
  const [rootDrop, setRootDrop] = useState(false);

  const handleRootDrop = useCallback((e) => {
    if (!drag.current) return;
    e.preventDefault();
    setRootDrop(false);
    // Soltar en el vacío del lienzo = agregar al final de la página.
    if (canDrop(drag.current.nodeType, 'root')) onDrop(null, 'end');
  }, [drag, onDrop]);

  return (
    <div className="bld-canvas-outer" onClick={() => onSelect(null)}>
      <div
        className={'bld-canvas bp-' + breakpoint + (rootDrop ? ' root-drop' : '')}
        style={Object.assign({ width: BP_WIDTH[breakpoint], maxWidth: '100%' }, cssVars)}
        onDragOver={(e) => {
          if (!drag.current) return;
          e.preventDefault();
          setRootDrop(true);
        }}
        onDragLeave={() => setRootDrop(false)}
        onDrop={handleRootDrop}
      >
        {(tree || []).length === 0 ? (
          <div className="bld-blank">
            <h3>Tu página está en blanco</h3>
            <p>Arrastra una sección prearmada desde la izquierda, o empieza con una en blanco.</p>
            <button type="button" onClick={(e) => { e.stopPropagation(); actions.addBlankSection(); }}>
              + Agregar sección
            </button>
          </div>
        ) : (
          (tree || []).map((node) => (
            <NodeView
              key={node.id}
              node={node}
              parentType="root"
              breakpoint={breakpoint}
              selectedId={selectedId}
              hoverId={hoverId}
              drag={drag}
              onSelect={onSelect}
              onHover={setHoverId}
              onDragState={onDragState}
              onDrop={onDrop}
              actions={actions}
              siteSlug={siteSlug}
              bookingSlug={bookingSlug}
            />
          ))
        )}
      </div>
    </div>
  );
}
